import { zodResolver } from '@hookform/resolvers/zod';
import {
  useForm,
  type DefaultValues,
  type FieldValues,
  type Path,
  type UseFormProps,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';

import { normalizeError } from '@/shared/api/errors';

/**
 * `useForm` pre-wired to a Zod schema, with the input/output types inferred
 * from the schema itself so the form values and the submit handler can never
 * drift from validation.
 */
export function useZodForm<TInput extends FieldValues, TOutput extends FieldValues>(
  schema: z.ZodType<TOutput, TInput>,
  options?: Omit<UseFormProps<TInput, unknown, TOutput>, 'resolver'> & {
    defaultValues?: DefaultValues<TInput>;
  },
): UseFormReturn<TInput, unknown, TOutput> {
  return useForm<TInput, unknown, TOutput>({
    resolver: zodResolver(schema),
    // Validate once the user has committed to a value, then keep it live —
    // validating on every keystroke from the start is hostile.
    mode: 'onTouched',
    reValidateMode: 'onChange',
    ...options,
  });
}

/**
 * Push server-side validation failures (FastAPI 422) onto the matching form
 * fields, falling back to the form-level error for anything unmatched.
 *
 * Client validation mirrors the backend's rules, so this is a safety net for
 * the constraints only the server can check (uniqueness, cross-entity rules) —
 * not a substitute for schema validation.
 */
export function applyServerErrors<
  TFieldValues extends FieldValues,
  TTransformed extends FieldValues,
>(
  form: UseFormReturn<TFieldValues, unknown, TTransformed>,
  error: unknown,
  { knownFields }: { knownFields?: ReadonlyArray<Path<TFieldValues>> } = {},
): void {
  const apiError = normalizeError(error);
  const entries = Object.entries(apiError.fieldErrors);

  let matchedAny = false;
  for (const [field, message] of entries) {
    const path = field as Path<TFieldValues>;
    if (knownFields && !knownFields.includes(path)) continue;
    form.setError(path, { type: 'server', message });
    matchedAny = true;
  }

  if (!matchedAny) {
    form.setError('root.serverError', { type: 'server', message: apiError.message });
  }
}
